import { ICardElementWithChildren } from "backend/model/model";
import SubElements from "components/subelements/SubElements";
import FileUploadInput from "qpr-base/ui/components/FileUploadInput";
import RichTextInput from "qpr-base/ui/components/RichTextInput";
import { makeThemedStyles } from "qpr-base/viewcomponents/utils";
import React from "react";
import FormButton from "../buttons/FormButton";
import InputContainer from "./InputContainer";
import TextInput from "./TextInput";
import { t } from "i18next";

import { TextField } from "@material-ui/core";
import { FieldValue, InputProp } from "backend/datainput";
import { If } from "react-if";

const useStyles = makeThemedStyles("ValueInputForm", (theme) => ({
  root: {
    background: "rgb(255,255,255)",
    padding: "0px",
    paddingTop: "20px",
    marginTop: "-6px",
    borderRadius: "20px",
    boxShadow: "0px 0px 2px rgba(215,235,222, 1)",
    width: "100%",
    display: "flex",
    flexWrap: "wrap",
    flexDirection: "row",
  },
  unitSymbol: {
    fontSize: "14px",
  },
  attachments: {
    marginTop: 0,
  },
  datetime: {
    "& .MuiInput-input": {
      fontSize: "16px",
      border: "1px solid lightgray",
      padding: "10px",
      borderRadius: "10px",
    },
  },
}));

export type ButtonProp = {
  isDisabled?: boolean;
  tag: string;
  style: "submit" | "save" | "approve" | "reject" | "decline";
  text: string | JSX.Element;
  onClick?: (values: FieldValue[]) => void;
};

type Props = {
  element: ICardElementWithChildren;
  childElements: ICardElementWithChildren[];
  fields: InputProp[];
  buttons: ButtonProp[];
  onChange?: (values: FieldValue[]) => void;
  onIntermediateChange?: (values: FieldValue[]) => void;
  onSubElementChange?: (data: { value: string, id: string, name: string }) => void;
};

const ValueInputForm = (props: Props) => {
  const classes = useStyles();
  const [formState, setFormState] = React.useState<FieldValue[]>(() => {
    let fieldId = 1;
    return props.fields.map((prop) => {
      fieldId += 1;
      return {
        id: String(fieldId),
        series: prop.series,
        value:
          typeof prop.initialValue !== "undefined"
            ? prop.initialValue
            : prop.series
            ? String(prop.element?.[prop.series] || "")
            : "",
        field: prop,
      };
    });
  });

  const [reRender, setReRender] = React.useState(1);
  const fieldErrors = React.useRef<Map<FieldValue, string[]>>(
    new Map<FieldValue, string[]>()
  );

  React.useEffect(() => {
    if (props.onIntermediateChange) {
      props.onIntermediateChange(formState);
    }
  }, [formState]);

  const isSubElementsReadOnly = props.buttons.find(b => b.tag === "submit") ? false : true;

  return (
    <div className={classes.root}>
      {formState.map((prop) => {
        if (prop.field.isHidden) return null;
        const key = JSON.stringify([prop.series, prop.id, prop.field.element?.id]);
        const errorMessages = fieldErrors.current.get(prop);

        if (prop.field.inputType === "info") {
          return <InputContainer errorMessages={errorMessages} title={prop.field.title || ""} key={key} />;
        }
        if (prop.field.inputType === "attachments") {
          return (
            <InputContainer key={key} title={prop.field.title} width="100%" errorMessages={errorMessages}>
              <div className={classes.attachments}>
                <FileUploadInput
                  maxFiles={process.env.ATTACHMENTS_MAX_FILE_COUNT ? parseInt(process.env.ATTACHMENTS_MAX_FILE_COUNT) : 5}
                  maxSize={process.env.ATTACHMENTS_MAX_FILE_SIZE ? parseInt(process.env.ATTACHMENTS_MAX_FILE_SIZE) : 10}
                  accept={process.env.ATTACHMENTS_ALLOWED_FILE_FORMATS ? process.env.ATTACHMENTS_ALLOWED_FILE_FORMATS.split(",") : [".pdf", ".doc", ".docx", ".ppt", ".pptx", ".xls", ".xlsx"]}
                  disabled={prop.field.isDisabled}
                  onFiles={(files: File[]) => {
                    setFormState((oldState) => oldState.map(fv => fv.id === prop.id ? { ...fv, files } : fv));
                  }}
                  files={prop.field.initialFiles}
                />
              </div>
            </InputContainer>
          );
        }
        if (prop.field.inputType === "comment") {
          return (
            <InputContainer title={prop.field.title || ""} key={key} width="100%" errorMessages={errorMessages}>
              <RichTextInput
                value={prop.field.initialValue}
                disabled={prop.field.isDisabled}
                onChange={(newValue) => {
                  setFormState((oldState) => oldState.map(fv => fv.id === prop.id ? { ...fv, value: newValue } : fv));
                }}
              />
            </InputContainer>
          );
        }
        if (prop.field.inputType === "datetime") {
          return (
            <InputContainer title={prop.field.title || prop.field.series || ""} key={key} errorMessages={errorMessages}>
              <TextField
                fullWidth={prop.field.fullWidth}
                disabled={prop.field.isDisabled}
                className={classes.datetime}
                type="date"
                value={prop.value}
                onChange={(e) => {
                   const newVal = e.target.value;
                   setFormState((oldState) => oldState.map(fv => fv.id === prop.id ? { ...fv, value: newVal } : fv));
                }}
              />
              <If condition={prop.field.unitSeries}>
                <div className={classes.unitSymbol}>
                  {prop.field.element?.[prop.field.unitSeries || "ACT_unitname"] || ""}
                </div>
              </If>
            </InputContainer>
          );
        }
        if (prop.field.inputType === "string") {
          return (
            <InputContainer title={prop.field.title || prop.field.series || ""} key={key} errorMessages={errorMessages} width={prop.field.fullWidth ? "80%" : undefined}>
              <TextInput
                fullWidth={prop.field.fullWidth}
                disabled={prop.field.isDisabled}
                value={prop.value}                
                onChange={(newValue) => {
                  setFormState((oldState) => oldState.map(fv => fv.id === prop.id ? { ...fv, value: newValue } : fv));
                }}
              />
              <If condition={prop.field.unitSeries}>
                <div className={classes.unitSymbol}>
                  {prop.field.element?.[prop.field.unitSeries || "ACT_unitname"] || ""}
                </div>
              </If>
            </InputContainer>
          );
        }
      })}
      {props.childElements.length ? (
        <InputContainer
          title={t("kpis.Sub Elements")}
          key={props.element.id}
          errorMessages={[]}
          width="100%"
        >
          <SubElements
            isDisabled={isSubElementsReadOnly}
            handleCalculator={async () => {}}
            elements={props.childElements}
            updateResult={(val: any) => {
              if (props.onSubElementChange) props.onSubElementChange(val);
            }}
            key={props.element.id}
          />
        </InputContainer>
      ) : null}
      <InputContainer width="100%">
        {props.buttons.filter((btn) => !btn.isDisabled).map((btn) => (
          <FormButton
            style={btn.style}
            type="button"
            onClick={() => {
              let failedFieldsCount = 0;
              formState.forEach((state) => {
                if (state.field.validate) {
                  const verifyResult = state.field.validate({ field: state, allFields: formState, actionTag: btn.tag });
                  if (verifyResult.success) fieldErrors.current.delete(state);
                  else { fieldErrors.current.set(state, verifyResult.errorMessages || []); failedFieldsCount += 1; }
                }
              });
              if (failedFieldsCount > 0) setReRender((v) => v + 1);
              if (failedFieldsCount === 0 && btn.onClick) btn.onClick(formState.filter((i) => !i.field.isDisabled));
            }}
          >
            {btn.text}
          </FormButton>
        ))}
      </InputContainer>
    </div>
  );
};

export default ValueInputForm;